import { Control, Controller, UseFormResetField, UseFormWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

// local imports
import avatarIcon from 'assets/icons/avatar.svg'
import branchList from 'assets/icons/list-branch.svg'
import gymIcon from 'assets/icons/gym-gray.svg'
import {
  GymListTypes,
  ICalendarGymSearchForm,
  IFilteredGymGroupList,
  IFilteredGymSalonList,
  IFilteredGymTrainerList,
} from 'types/GymType'
import {
  DropdownWrapper,
  PriceInfo,
  StyledCustomDropdown,
} from 'components/home/on-campus-services/coiffeur/coiffeur-search-modal/styled'

interface IGymSearchFormProps {
  control: Control<ICalendarGymSearchForm>
  watch: UseFormWatch<ICalendarGymSearchForm>
  resetField: UseFormResetField<ICalendarGymSearchForm>
  locationList: any[] | undefined
  gymSalonList: IFilteredGymSalonList[] | undefined
  groupLessonList: IFilteredGymGroupList[] | undefined
  trainerList: IFilteredGymTrainerList[] | undefined
}

/* #################### Search Form Component #################### */
export default function CalendarGymSearchForm({
  control,
  resetField,
  watch,
  locationList,
  gymSalonList,
  groupLessonList,
  trainerList,
}: IGymSearchFormProps) {
  const { t } = useTranslation()
  const resTypeOptions: { id: GymListTypes; name: string }[] = [
    { id: 'personal', name: t('input-texts.personal-work') },
    { id: 'group', name: t('input-texts.group-lesson') },
    { id: 'trainer', name: t('input-texts.pt-work') },
  ]
  /* Variables */
  const resTypeVal = watch('reservationType')
  const trainerVal = watch('trainer')
  const trainerPrice = !!trainerVal?.price ? trainerVal.priceStr : t('gym-res-modal.price-free')

  function groupLessonOptTemp(options: IFilteredGymGroupList) {
    const groupLessonPrice = !!options?.price ? options?.priceStr : t('gym-res-modal.price-free')
    return (
      <StyledGymGroupDdItems>
        <div className="group-lesson-info">
          <div className="group-lesson-name-time">
            <span className="lesson-name">{options.gymLessonType.name}</span>
            <span className="lesson-time">
              {options.startHourStr} - {options.endHourStr}
            </span>
          </div>
          <div className="trainer-name">{options.gymTrainer.fullName}</div>
          <div className="price">{groupLessonPrice}</div>
        </div>
      </StyledGymGroupDdItems>
    )
  }

  return (
    <div className="d-flex flex-column gap-2" id="searchFormWrapper">
      {/* Location */}
      <Controller
        name="locationId"
        control={control}
        render={({ field }) => (
          <DropdownWrapper>
            <label className="autocomplete-title">{t('most-used-words.location')}</label>
            <i className="autocomplete-icon p-input-icon-left pi pi-map-marker"></i>
            <StyledCustomDropdown
              id="coiffeurSalonLocation"
              emptyMessage={t('most-used-words.option-not-found')}
              emptyFilterMessage={t('most-used-words.option-not-found')}
              name="locationId"
              placeholder={t('input-texts.choose-location')}
              optionLabel="name"
              optionValue="id"
              valueTemplate={(opt, props) => valueTemplate(opt, props)}
              options={locationList}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
            />
          </DropdownWrapper>
        )}
      />

      {/* GYM Reservation Types [personal - group - trainer] */}
      <Controller
        name="reservationType"
        control={control}
        render={({ field }) => (
          <DropdownWrapper>
            <label className="autocomplete-title">{t('input-texts.gym-res-type')}</label>
            <img className="autocomplete-icon p-input-icon-left" src={branchList} alt="logo" width={18} height={18} />
            <StyledCustomDropdown
              id="gymReservationType"
              emptyMessage={t('most-used-words.option-not-found')}
              emptyFilterMessage={t('most-used-words.option-not-found')}
              name="reservationType"
              placeholder={t('input-texts.choose-res-type')}
              optionLabel="name"
              optionValue="id"
              valueTemplate={(opt, props) => valueTemplate(opt, props)}
              options={resTypeOptions}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.value)
                resetField('gymSalon')
                resetField('groupLesson')
                resetField('trainer')
              }}
            />
          </DropdownWrapper>
        )}
      />

      {/* Gym Salon (Personal) */}
      {resTypeVal === 'personal' && (
        <Controller
          name="gymSalon"
          control={control}
          render={({ field }) => (
            <DropdownWrapper>
              <label className="autocomplete-title">{t('input-texts.gym-salon')}</label>
              <img className="autocomplete-icon p-input-icon-left" src={gymIcon} alt="logo" width={20} height={20} />
              <StyledCustomDropdown
                id="gymSalonDropdown"
                name="gymSalon"
                emptyMessage={t('most-used-words.option-not-found')}
                appendTo="self"
                placeholder={t('input-texts.choose-gym-salon')}
                optionLabel="name"
                options={gymSalonList}
                valueTemplate={(opt, props) => valueTemplate(opt, props)}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
              />
            </DropdownWrapper>
          )}
        />
      )}

      {/* Gym Group Lesson (Group) */}
      {resTypeVal === 'group' && (
        <Controller
          name="groupLesson"
          control={control}
          render={({ field }) => (
            <DropdownWrapper>
              <label className="autocomplete-title">{t('input-texts.gym-group-lesson')}</label>
              <img className="autocomplete-icon p-input-icon-left" src={gymIcon} alt="logo" width={18} height={18} />
              <StyledCustomDropdown
                id="gymGroupLesson"
                className="gym-group-dropdown"
                emptyMessage={t('most-used-words.option-not-found')}
                emptyFilterMessage={t('most-used-words.option-not-found')}
                name="groupLesson"
                placeholder={t('input-texts.choose-group-lesson')}
                optionLabel="gymLessonType.name"
                valueTemplate={(opt, props) => valueTemplate(opt, props, 'group')}
                itemTemplate={groupLessonOptTemp}
                options={groupLessonList}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.value)
                }}
              />
            </DropdownWrapper>
          )}
        />
      )}

      {/* Gym Trainer */}
      {resTypeVal === 'trainer' && (
        <>
          <Controller
            name="trainer"
            control={control}
            render={({ field }) => (
              <DropdownWrapper>
                <label className="autocomplete-title">{t('input-texts.gym-trainer')}</label>
                <img
                  className="autocomplete-icon p-input-icon-left"
                  src={avatarIcon}
                  alt="logo"
                  width={18}
                  height={18}
                />
                <StyledCustomDropdown
                  id="gymTrainer"
                  emptyMessage={t('most-used-words.option-not-found')}
                  emptyFilterMessage={t('most-used-words.option-not-found')}
                  name="trainer"
                  placeholder={t('input-texts.choose-gym-trainer')}
                  optionLabel="gymTrainer.fullName"
                  valueTemplate={(opt, props) => valueTemplate(opt, props, 'trainer')}
                  options={trainerList}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.value)
                  }}
                />
              </DropdownWrapper>
            )}
          />

          {!!trainerVal && (
            <PriceInfo className="mt-2">
              <i className="pi pi-info-circle" />
              <span>
                {t('gym-res-modal.price-info')} : <span className="price"> {trainerPrice}</span>
              </span>
            </PriceInfo>
          )}
        </>
      )}
    </div>
  )
}

function valueTemplate(options: any, props: any, type?: string) {
  if (type === 'group')
    return (
      <div className="selectedVal">
        {options?.gymLessonType?.name
          ? `${options?.gymLessonType.name} | ${options?.gymTrainer?.fullName} | ${options.startHourStr} - ${options.endHourStr}`
          : props?.placeholder}
      </div>
    )
  if (type === 'trainer') {
    return <div className="selectedVal">{options?.gymTrainer?.fullName || props?.placeholder}</div>
  }
  return <div className="selectedVal">{options?.name || props?.placeholder}</div>
}

const StyledGymGroupDdItems = styled.div`
  padding: 0 0 0.3rem;
  border-bottom: 1px dotted #7c7c7c;

  .group-lesson-info {
    color: #363636;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;

    .group-lesson-name-time {
      flex: 1 0 auto;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      column-gap: 4px;

      .lesson-name {
        font-size: 16px;
        line-height: 20px;
        font-weight: 600;
      }

      .lesson-time {
        line-height: 20px;
        font-size: 13px;
      }
    }

    .trainer-name {
      font-size: 14px;
    }
    .price {
      font-size: 14px;
    }
  }
`
